'use client';

import { Button, Spinner } from '@common/components';
import {
  SvgBookmark,
  SvgBookmarkFill,
  SvgNavigationArrowOn,
  SvgPhone,
  SvgShareLink,
} from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { AuthQueryOptions, BOOKMARK_TYPE } from '@/api';
import { IS_DEV } from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useLocation } from '@/hooks/useLocation';
import { CUSTOM_SCHEME } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';

interface ButtonListProps {
  title: string;
  tel: string;
  longitude: number;
  latitude: number;
  emergencyRoomId: number;
}

export const ButtonList = ({
  title,
  tel,
  latitude,
  longitude,
  emergencyRoomId,
}: ButtonListProps) => {
  const { isWelloApp } = useCheckWelloApp();
  const isMobile = useDeviceInfoStore((state) =>
    ['android', 'ios'].includes(state.osType ?? ''),
  );

  const setShareLink = useGlobalStore((state) => state.setShareLink);
  const location = useLocation({ enabledInitialFetch: true });

  const { bookmark } = useBookmark();

  const isLogin = useAuthStore((state) => !!state.isLogin);

  const { data: isBookmarked = false } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/emergency-room/{emergencyRoomId}']({
      path: {
        emergencyRoomId,
      },
    }),

    enabled: isLogin,
    select: ({ context }) => context?.wishlistYN,
  });

  const fromLongitude = location.data?.longitude?.toString();
  const fromLatitude = location.data?.latitude?.toString();

  const navigationUrl = useMemo(() => {
    if (!fromLongitude || !fromLatitude) return;

    const navigationUrl = new URL('http://map.naver.com');

    if (isMobile) {
      navigationUrl.pathname += '/p/directions';
    } else {
      navigationUrl.pathname += '/directions';
    }

    navigationUrl.searchParams.set('slng', fromLongitude);
    navigationUrl.searchParams.set('slat', fromLatitude);
    navigationUrl.searchParams.set('stext', '내위치');
    navigationUrl.searchParams.set('elng', longitude.toString());
    navigationUrl.searchParams.set('elat', latitude.toString());
    navigationUrl.searchParams.set('etext', title);
    navigationUrl.searchParams.set('menu', 'route');

    let href = navigationUrl.href;

    if (isWelloApp) {
      href = CUSTOM_SCHEME.EXTERNAL_PREFIX + href;
    }

    return href;
  }, [
    fromLatitude,
    fromLongitude,
    isMobile,
    isWelloApp,
    latitude,
    longitude,
    title,
  ]);

  return (
    <ul className="flex items-center justify-between border-y border-Gray-200">
      <li className="max-w-8 flex-1" />
      <li>
        <Button
          className="flex h-fit w-62 flex-col gap-8 whitespace-nowrap px-0 font-400 text-Gray-800 typo-body4"
          theme="primary-text"
          onClick={() => {
            window.location.href = `tel:${tel}`;
          }}
        >
          <SvgPhone className="size-20 text-Gray-700" />
          전화
        </Button>
      </li>
      <li className="flex-1" />
      <li>
        <a
          className="flex h-fit w-62 flex-col gap-8 whitespace-nowrap px-0 font-400 text-Gray-800 flex-center typo-body4"
          href={navigationUrl}
          rel="noreferrer"
          target={IS_DEV ? '_self' : '_blank'}
          onClick={(e) => {
            if (!navigationUrl) return e.preventDefault();

            if (isMobile) {
              let href = `nmap://route/public?slat=${fromLatitude}&slng=${fromLongitude}&dlat=${latitude}&dlng=${longitude}&dname=${title}`;

              if (isWelloApp) {
                href = CUSTOM_SCHEME.EXTERNAL_PREFIX + href;
              }

              window.location.href = href;
            }
          }}
        >
          {navigationUrl ? (
            <SvgNavigationArrowOn className="size-20 text-Gray-700" />
          ) : (
            <Spinner size={20} />
          )}
          내비게이션
        </a>
      </li>
      <li className="flex-1" />
      <li>
        <Button
          className={cn(
            'flex h-fit w-62 flex-col gap-8 whitespace-nowrap px-0 font-400 typo-body4',
            isBookmarked ? 'text-B-500' : 'text-Gray-800',
          )}
          loading={isLogin && isBookmarked === undefined}
          theme="primary-text"
          onClick={() =>
            bookmark({
              bookmarkType: BOOKMARK_TYPE.EMERGENCY_ROOM,
              contentId: emergencyRoomId,
              isBookmarked: isBookmarked ?? false,
              contentName: title,
            })
          }
        >
          {isBookmarked ? (
            <SvgBookmarkFill className="size-20 text-B-500" />
          ) : (
            <SvgBookmark className="size-20 text-Gray-700" />
          )}
          스크랩
        </Button>
      </li>
      <li className="flex-1" />
      <li>
        <Button
          className="flex h-fit w-62 flex-col gap-8 whitespace-nowrap px-0 font-400 text-Gray-800 typo-body4"
          theme="primary-text"
          onClick={() => {
            setShareLink(window.location.href);
          }}
        >
          <SvgShareLink className="size-20 text-Gray-700" />
          공유
        </Button>
      </li>
      <li className="max-w-8 flex-1" />
    </ul>
  );
};
